import React, { useEffect, useState } from "react";
import Newsletter from '../components/Newsletter.jsx';
import { Helmet } from "react-helmet";
import { FaShareAlt, FaFacebook, FaWhatsapp, FaTwitter, FaLink, FaEye, FaCalendarAlt, FaUser } from "react-icons/fa";
import fallbackImage1 from "../assets/hero.webp";
import fallbackImage2 from "../assets/hero.jpg";
import heroImage from "../assets/hero.webp";
import HeartbeatLoader from '../components/HeartbeatLoader';

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageUrls, setImageUrls] = useState({});
  const [showShare, setShowShare] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const blogsPerPage = 12;

  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  const findValidImageUrl = async (postId) => {
    const url = `https://www.pcds.co.in/cpm/imagesarticle/b${postId}.png`;
    const exists = await checkImageExists(url);
    return exists ? url : (Math.random() > 0.5 ? fallbackImage1 : fallbackImage2);
  };

  const getBlogUrl = (blog) => `/article/${blog.url}.php`;

  const toggleShare = (id) => {
    setShowShare((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const shareBlog = (platform, blog) => {
    const blogUrl = `${window.location.origin}${getBlogUrl(blog)}`;
    const shareText = `Check out this medical article: ${blog.title}`;
    const ogImageUrl = imageUrls[blog.id] || heroImage;

    switch (platform) {
      case "facebook":
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(blogUrl)}&picture=${encodeURIComponent(ogImageUrl)}`);
        break;
      case "whatsapp":
        window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${blogUrl}`)}`);
        break;
      case "twitter":
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(blogUrl)}`);
        break;
      case "copy":
        navigator.clipboard.writeText(blogUrl).then(() => {
          alert("Link copied to clipboard!");
        });
        break;
      default:
        break;
    }

    setShowShare({});
  };

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setIsLoading(true);
        const res = await fetch("https://www.pcds.co.in/blogapi.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            uri: "get",
            website: "http://www.thehealing.in",
            page: 1
          }),
        });
        const data = await res.json();

        const processed = data.map((blog) => ({
          ...blog,
          title: (blog.title || "Untitled Article").replace(/&ndash;/g, '–'),
          excerpt: blog.body?.replace(/<[^>]*>?/gm, '').substring(0, 100) + '...' || 'Read more...',
          date: blog.datec
            ? new Date(blog.datec).toLocaleDateString("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "Unknown date",
          viewCount: blog.viewcount || 0,
          keywords: blog.keywords || "",
          created_at: blog.created_at || new Date().toISOString(),
          url: blog.url || blog.id.toString(),
          seoTitle: blog.title ? `${blog.title.replace(/&ndash;/g, '–')} | Healthcare Blog` : "Healthcare Blog | Latest Medical News and Tips",
          seoDescription: blog.body 
            ? blog.body.replace(/<[^>]+>/g, '').substring(0, 160) 
            : "Get the latest medical news, health tips, and research updates. Useful information for doctors, patients, and healthcare professionals.",
          seoKeywords: blog.keywords || "medical, healthcare, doctor, patient, medicine, health tips, hospital",
        }));

        setBlogs(processed);

        // Preload image for the first blog (hero section)
        if (processed.length > 0) {
          const heroImgUrl = await findValidImageUrl(processed[0].id);
          setImageUrls(prev => ({...prev, [processed[0].id]: heroImgUrl}));
        }

        // Preload images for the first page
        const imgObj = {};
        const firstBlogs = processed.slice(0, blogsPerPage);
        for (const blog of firstBlogs) {
          imgObj[blog.id] = await findValidImageUrl(blog.id);
        }
        setImageUrls(imgObj);
      } catch (e) {
        console.error("Error:", e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  useEffect(() => {
    const preloadImages = async () => {
      const start = (currentPage - 1) * blogsPerPage;
      const currentBlogs = blogs.slice(start, start + blogsPerPage);

      const imgObj = { ...imageUrls };
      for (const blog of currentBlogs) {
        if (!imgObj[blog.id]) {
          imgObj[blog.id] = await findValidImageUrl(blog.id);
        }
      }
      setImageUrls(imgObj);
    };

    if (blogs.length) preloadImages();
  }, [currentPage, blogs]);

  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = blogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(blogs.length / blogsPerPage);
  const paginate = (page) => setCurrentPage(page);

  const formatViewCount = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  if (isLoading) {
    return (
      <div className="bg-[#f8faf8] min-h-screen">
        <HeartbeatLoader />
      </div>
    );
  }

  return (
    <div className="bg-[#f8faf8] min-h-screen">
      <Helmet>
        <title>{blogs.length > 0 ? blogs[0].seoTitle : "Healthcare Blog | Latest Medical News and Tips"}</title>
        <meta
          name="description"
          content={blogs.length > 0 ? blogs[0].seoDescription : "Get the latest medical news, health tips, and research updates."}
        />
        <meta 
          name="keywords" 
          content={blogs.slice(0, 5).map(blog => blog.keywords).filter(Boolean).join(', ')} 
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content={blogs.length > 0 ? blogs[0].seoTitle : "Healthcare Blog | Latest Medical News and Tips"} />
        <meta property="og:description" content={blogs.length > 0 ? blogs[0].seoDescription : "Get the latest medical news, health tips, and research updates."} />
        <meta property="og:image" content={blogs.length > 0 ? imageUrls[blogs[0].id] || heroImage : heroImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Healthcare Blog" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blogs.length > 0 ? blogs[0].seoTitle : "Healthcare Blog | Latest Medical News and Tips"} />
        <meta name="twitter:description" content={blogs.length > 0 ? blogs[0].seoDescription : "Get the latest medical news, health tips, and research updates."} />
        <meta name="twitter:image" content={blogs.length > 0 ? imageUrls[blogs[0].id] || heroImage : heroImage} />
        <link rel="canonical" href={window.location.href} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": blogs.slice(0, 5).map((blog, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "Article",
                "url": `${window.location.origin}${getBlogUrl(blog)}`,
                "name": blog.title,
                "description": blog.excerpt,
                "datePublished": blog.created_at,
                "image": imageUrls[blog.id] || heroImage,
                "author": {
                  "@type": "Person",
                  "name": blog.username || "Medical Author"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Healthcare Blog",
                  "logo": {
                    "@type": "ImageObject",
                    "url": `${window.location.origin}/logo.png`
                  }
                }
              }
            }))
          })}
        </script>
      </Helmet>

      {/* Hero Section with Featured Post */}
      <div className="relative">
        {blogs.length > 0 && (
          <div className="w-full h-[80vh] min-h-[500px] max-h-[700px]">
            <img
              src={imageUrls[blogs[0].id] || heroImage}
              alt={blogs[0].title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-70"></div>
            <div className="absolute inset-0 container mx-auto px-4 flex items-end pb-16">
              <div className="max-w-4xl text-white z-10">
                <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                  {blogs[0].title}
                </h1>
                <div className="flex items-center space-x-4 text-gray-200">
                  <span className="flex items-center">
                    <FaUser className="mr-2" /> {blogs[0].username || "Medical Expert"}
                  </span>
                  <span className="flex items-center">
                    <FaCalendarAlt className="mr-2" /> {formatDate(blogs[0].created_at)}
                  </span>
                  <span className="flex items-center">
                    <FaEye className="mr-2" /> {formatViewCount(blogs[0].viewCount)} views
                  </span>
                </div>
                <a
                  href={getBlogUrl(blogs[0])}
                  className="mt-6 inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-300"
                >
                  Read Full Article
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Top Trending Articles Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 pb-2 border-b">Top Trending Medical Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {currentBlogs.slice(1, 4).map((blog) => (
              <div key={blog.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="relative h-60 overflow-hidden">
                  <img
                    src={imageUrls[blog.id] || fallbackImage1}
                    alt={blog.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  <button
                    onClick={() => toggleShare(blog.id)}
                    className="absolute top-3 right-3 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition-colors"
                  >
                    <FaShareAlt className="text-gray-700" />
                  </button>
                  {showShare[blog.id] && (
                    <div className="absolute top-14 right-3 bg-white rounded-lg shadow-lg p-2 flex flex-col space-y-2 z-10">
                      <button
                        onClick={() => shareBlog("facebook", blog)}
                        className="flex items-center text-blue-600 text-sm hover:underline px-2 py-1 rounded hover:bg-blue-50"
                      >
                        <FaFacebook className="mr-2" /> Facebook
                      </button>
                      <button
                        onClick={() => shareBlog("whatsapp", blog)}
                        className="flex items-center text-green-600 text-sm hover:underline px-2 py-1 rounded hover:bg-green-50"
                      >
                        <FaWhatsapp className="mr-2" /> WhatsApp
                      </button>
                      <button
                        onClick={() => shareBlog("twitter", blog)}
                        className="flex items-center text-blue-400 text-sm hover:underline px-2 py-1 rounded hover:bg-blue-50"
                      >
                        <FaTwitter className="mr-2" /> Twitter
                      </button>
                      <button
                        onClick={() => shareBlog("copy", blog)}
                        className="flex items-center text-gray-700 text-sm hover:underline px-2 py-1 rounded hover:bg-gray-50"
                      >
                        <FaLink className="mr-2" /> Copy Link
                      </button>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center text-xs text-gray-500 mb-2">
                    <span className="flex items-center mr-3">
                      <FaUser className="mr-1" /> {blog.username || "Medical Expert"}
                    </span>
                    <span className="flex items-center">
                      <FaCalendarAlt className="mr-1" /> {formatDate(blog.created_at)}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-blue-600 transition-colors">
                    <a href={getBlogUrl(blog)}>{blog.title}</a>
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{blog.excerpt}</p>
                  <div className="flex justify-between items-center">
                    <a
                      href={getBlogUrl(blog)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Read More →
                    </a>
                    <span className="flex items-center text-xs text-gray-500">
                      <FaEye className="mr-1" /> {formatViewCount(blog.viewCount)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* More Articles Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b pb-2">More Healthcare Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {currentBlogs.slice(4).map((blog) => (
              <div 
                key={blog.id} 
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 transform hover:-translate-y-1"
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={imageUrls[blog.id] || fallbackImage1}
                    alt={blog.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  <button
                    onClick={() => toggleShare(blog.id)}
                    className="absolute top-2 right-2 bg-white p-1.5 rounded-full shadow-md hover:bg-gray-100 transition-colors"
                  >
                    <FaShareAlt className="text-gray-700 text-sm" />
                  </button>
                  {showShare[blog.id] && (
                    <div className="absolute top-10 right-2 bg-white rounded-lg shadow-lg p-2 flex flex-col space-y-2 z-10">
                      <button
                        onClick={() => shareBlog("facebook", blog)}
                        className="flex items-center text-blue-600 text-xs hover:underline px-2 py-1 rounded hover:bg-blue-50"
                      >
                        <FaFacebook className="mr-2" /> Facebook
                      </button>
                      <button
                        onClick={() => shareBlog("whatsapp", blog)}
                        className="flex items-center text-green-600 text-xs hover:underline px-2 py-1 rounded hover:bg-green-50"
                      >
                        <FaWhatsapp className="mr-2" /> WhatsApp
                      </button>
                      <button
                        onClick={() => shareBlog("twitter", blog)}
                        className="flex items-center text-blue-400 text-xs hover:underline px-2 py-1 rounded hover:bg-blue-50"
                      >
                        <FaTwitter className="mr-2" /> Twitter
                      </button>
                      <button
                        onClick={() => shareBlog("copy", blog)}
                        className="flex items-center text-gray-700 text-xs hover:underline px-2 py-1 rounded hover:bg-gray-50"
                      >
                        <FaLink className="mr-2" /> Copy Link
                      </button>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 hover:text-blue-600 transition-colors">
                    <a href={getBlogUrl(blog)}>{blog.title}</a>
                  </h3>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{formatDate(blog.created_at)}</span>
                    <span className="flex items-center">
                      <FaEye className="mr-1" /> {formatViewCount(blog.viewCount)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination */}
        <div className="flex justify-center mt-12">
          <nav className="flex items-center space-x-2">
            <button
              onClick={() => paginate(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => paginate(pageNum)}
                  className={`px-4 py-2 rounded-md ${
                    currentPage === pageNum
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Next
            </button>
          </nav>
        </div>
      </div>

      {/* Newsletter Section */}
      <Newsletter />
    </div>
  );
};

export default Blog;